/**
 * Card frame generator — Style Bible §4.
 *
 * The frame, plates, rarity marks and type icons are generated; only the artwork
 * window is left for an illustration to fill. That split is what makes variant
 * cards free: `The Wizard — Saved` is the same frame and the same artwork with an
 * overlay on top, never a second illustration.
 */

import {
  CARD_SPEC,
  CARD_TYPE_INK,
  HALFTONE,
  INK,
  LINE,
  MISREGISTRATION,
  PAPER,
  RARITY,
  SCREEN_ANGLE,
  SPOT,
  shade,
  type CardType,
  type Rarity,
} from './tokens.js';
import {
  ArtRng,
  circle,
  crackPath,
  defs,
  ellipse,
  group,
  halftonePattern,
  hashString,
  misregistered,
  n,
  paperGrain,
  path,
  polygon,
  roundedRectPath,
  svg,
  tag,
  text,
} from './svg.js';

export type CardOverlay = 'saved' | 'fallen' | 'cursed' | 'foil' | null;

export interface CardRenderOptions {
  readonly type: CardType;
  readonly rarity: Rarity;
  readonly name: string;
  readonly text: string;
  readonly overlay?: CardOverlay;
  /** Artwork href; when omitted, a procedural placeholder fills the window. */
  readonly artHref?: string;
  readonly scale?: number;
  readonly seed?: string;
}

const TYPE_GLYPH: Readonly<Record<CardType, string>> = {
  EVENT: 'M6 16 L16 6 L26 16 L16 26 Z',
  ACTION: 'M8 24 L16 6 L24 24 Z',
  FATE: 'M16 5 L19 13 L27 13 L21 18 L23 27 L16 22 L9 27 L11 18 L5 13 L13 13 Z',
  KNOWLEDGE: 'M7 8 h18 v16 h-18 Z M16 8 v16',
  MEMORY: 'M16 6 a10 10 0 1 1 -0.1 0 M16 11 v6 l4 3',
  RELATIONSHIP: 'M11 13 a4 4 0 1 1 8 0 M21 13 a4 4 0 1 1 8 0 M4 24 q12 -8 24 0',
  CURSE: 'M16 6 L26 26 H6 Z M16 13 v7 M16 23 v1',
  LEGEND: 'M6 26 L10 10 L16 18 L22 10 L26 26 Z',
};

function paperTexture(id: string, seed: string): string {
  return paperGrain(id, INK.key, seed);
}

/** Rarity frame variants (Style Bible §1.5). */
function rarityFrame(rarity: Rarity, seed: string): string {
  const { width: W, height: H, frameInset: F, radius } = CARD_SPEC;
  const color = RARITY[rarity].color;
  const inner = roundedRectPath(F, F, W - F * 2, H - F * 2, radius * 0.7);
  const base = path(inner, { fill: 'none', stroke: color, 'stroke-width': LINE.outline });

  switch (RARITY[rarity].frame) {
    case 'single':
      return base;
    case 'notched': {
      const s = 22;
      const notch = (x: number, y: number, dx: number, dy: number): string =>
        path(`M${n(x)} ${n(y + dy * s)} L${n(x)} ${n(y)} L${n(x + dx * s)} ${n(y)}`, {
          fill: 'none',
          stroke: color,
          'stroke-width': LINE.outline,
        });
      return (
        base +
        notch(F + 10, F + 10, 1, 1) +
        notch(W - F - 10, F + 10, -1, 1) +
        notch(F + 10, H - F - 10, 1, -1) +
        notch(W - F - 10, H - F - 10, -1, -1)
      );
    }
    case 'double':
      return (
        base +
        path(roundedRectPath(F + 9, F + 9, W - (F + 9) * 2, H - (F + 9) * 2, radius * 0.55), {
          fill: 'none',
          stroke: color,
          'stroke-width': LINE.structure,
          opacity: 0.8,
        })
      );
    case 'screened': {
      const dbl =
        base +
        path(roundedRectPath(F + 9, F + 9, W - (F + 9) * 2, H - (F + 9) * 2, radius * 0.55), {
          fill: 'none',
          stroke: color,
          'stroke-width': LINE.structure,
          opacity: 0.8,
        });
      // Corner filigree, one motif mirrored four ways.
      const curl = (x: number, y: number, sx: number, sy: number): string =>
        group(
          { transform: `translate(${n(x)} ${n(y)}) scale(${n(sx)} ${n(sy)})` },
          path('M0 26 q0 -26 26 -26 M4 26 q0 -22 22 -22', {
            fill: 'none',
            stroke: color,
            'stroke-width': LINE.structure,
            opacity: 0.85,
          }),
        );
      return (
        dbl +
        curl(F + 14, F + 14, 1, 1) +
        curl(W - F - 14, F + 14, -1, 1) +
        curl(F + 14, H - F - 14, 1, -1) +
        curl(W - F - 14, H - F - 14, -1, -1)
      );
    }
    case 'starred': {
      const gold = SPOT.yellow;
      const gilt =
        path(inner, { fill: 'none', stroke: gold, 'stroke-width': LINE.outline * 1.6 }) +
        path(roundedRectPath(F + 11, F + 11, W - (F + 11) * 2, H - (F + 11) * 2, radius * 0.5), {
          fill: 'none',
          stroke: shade(gold, 0.2),
          'stroke-width': LINE.detail,
          opacity: 0.9,
        });
      // A printed star burst at the foot, the way a premium card was marked.
      const cx = CARD_SPEC.width / 2;
      const cy = H - F - 30;
      const star = Array.from({ length: 5 }, (_, i) => {
        const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
        const b = ((i + 0.5) / 5) * Math.PI * 2 - Math.PI / 2;
        return [
          [cx + Math.cos(a) * 26, cy + Math.sin(a) * 26],
          [cx + Math.cos(b) * 11, cy + Math.sin(b) * 11],
        ] as [number, number][];
      }).flat();
      return gilt + polygon(star, { fill: gold, stroke: INK.key, 'stroke-width': LINE.structure });
    }
    case 'misregistered': {
      const rng = new ArtRng(`crack:${seed}`);
      const cracks = Array.from({ length: 4 }, (_, i) =>
        path(
          crackPath(
            rng,
            rng.range(F, W - F),
            F,
            rng.range(F, W - F),
            H - F,
            8,
            26,
          ),
          { fill: 'none', stroke: color, 'stroke-width': i === 0 ? LINE.structure : LINE.detail, opacity: 0.55 },
        ),
      ).join('');
      return (
        path(inner, { fill: 'none', stroke: color, 'stroke-width': LINE.outline * 1.4 }) + cracks
      );
    }
  }
}

/**
 * Procedural artwork for a `slot` asset (docs/04 §7).
 *
 * Every card type gets its OWN subject. Before this, all 48 frames printed the
 * same sunrise-and-mountains, which made the art window decorative rather than
 * informative — a CURSE and a LEGEND were indistinguishable at a glance, so the
 * slot may as well not have existed (절대 규칙 11).
 *
 * The construction is shared so the deck still reads as one printing: paper,
 * one screened plate in the type's ink, and a black key line over it. The
 * SUBJECT is what differs. When a real illustration arrives it replaces this
 * whole layer and nothing else changes.
 */
function artworkPlaceholder(type: CardType, seed: string): string {
  const { artwork: A } = CARD_SPEC;
  const rng = new ArtRng(`art:${seed}`);
  const ink = CARD_TYPE_INK[type];
  const uid = `${type.toLowerCase()}${hashString(`art:${seed}`).toString(36)}`;
  const spotId = `asp-${uid}`;
  const keyId = `aky-${uid}`;
  const keyDarkId = `akd-${uid}`;

  // Normalised coordinates inside the art window, so the drawings below read as
  // composition rather than as arithmetic.
  const ax = (u: number): number => A.x + A.w * u;
  const ay = (v: number): number => A.y + A.h * v;
  const cx = ax(0.5);

  /** A black key line. Every subject is held together by one. */
  const key = (d: string, w: number = LINE.structure): string =>
    path(d, {
      fill: 'none',
      stroke: INK.key,
      'stroke-width': w,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    });
  /** A shape on the key plate — solid black, the way a silhouette prints. */
  const solid = (d: string): string => path(d, { fill: INK.key });
  /** A shape on the colour plate, held by the key line (Style Bible §2.1). */
  const plate = (d: string, fill: string): string =>
    path(d, { fill }) + key(d, LINE.outline);

  const arc = (r: number, a0: number, a1: number, ox: number, oy: number): string =>
    `M${n(ox + Math.cos(a0) * r)} ${n(oy + Math.sin(a0) * r)}` +
    ` A${n(r)} ${n(r)} 0 0 1 ${n(ox + Math.cos(a1) * r)} ${n(oy + Math.sin(a1) * r)}`;

  // Two screens at DIFFERENT angles. Printed at the same angle the dots land on
  // top of each other and the second plate adds no density at all (§2.2).
  const screens = defs(
    halftonePattern(spotId, ink, HALFTONE.mid.dot, HALFTONE.mid.grid, SCREEN_ANGLE.spot) +
      halftonePattern(keyId, INK.key, HALFTONE.light.dot, HALFTONE.light.grid, SCREEN_ANGLE.key) +
      halftonePattern(keyDarkId, INK.key, HALFTONE.shadow.dot, HALFTONE.shadow.grid, SCREEN_ANGLE.key),
  );

  /** Sky over ground, with a hard printed horizon. */
  const horizonStage = (skyRatio = 0.62): string => {
    const hy = ay(skyRatio);
    return (
      tag('rect', { x: A.x, y: A.y, width: A.w, height: A.h * skyRatio, fill: PAPER.aged }) +
      tag('rect', { x: A.x, y: A.y, width: A.w, height: A.h * skyRatio, fill: `url(#${spotId})` }) +
      tag('rect', { x: A.x, y: hy, width: A.w, height: A.y + A.h - hy, fill: PAPER.box }) +
      key(`M${n(A.x)} ${n(hy)} h${n(A.w)}`, LINE.outline)
    );
  };

  /** A ruled panel — for subjects that are objects, not places. */
  const panelStage = (): string => {
    const m = 16;
    return (
      tag('rect', { x: A.x, y: A.y, width: A.w, height: A.h, fill: PAPER.aged }) +
      tag('rect', { x: A.x + m, y: A.y + m, width: A.w - m * 2, height: A.h - m * 2, fill: `url(#${spotId})` }) +
      tag('rect', {
        x: A.x + m,
        y: A.y + m,
        width: A.w - m * 2,
        height: A.h - m * 2,
        fill: 'none',
        stroke: INK.key,
        'stroke-width': LINE.structure,
      })
    );
  };

  let stage: string;
  let subject: string;

  switch (type) {
    case 'EVENT': {
      // A struck bell. An event is something the world hears.
      stage = horizonStage();
      const by = ay(0.66);
      const top = ay(0.24);
      // 종은 아래로 갈수록 벌어진다. 이걸 빼면 전구나 골무로 읽힌다.
      const body =
        `M${n(cx - 100)} ${n(by)} C${n(cx - 96)} ${n(top + 82)} ${n(cx - 56)} ${n(top + 30)} ${n(cx - 46)} ${n(top + 4)}` +
        ` Q${n(cx)} ${n(top - 26)} ${n(cx + 46)} ${n(top + 4)}` +
        ` C${n(cx + 56)} ${n(top + 30)} ${n(cx + 96)} ${n(top + 82)} ${n(cx + 100)} ${n(by)} Z`;
      subject =
        [3, 4, 5]
          .map((i) => key(arc(58 + i * 40, Math.PI * 1.15, Math.PI * 1.42, cx, ay(0.46)), LINE.detail))
          .join('') +
        [3, 4, 5]
          .map((i) => key(arc(58 + i * 40, Math.PI * 1.58, Math.PI * 1.85, cx, ay(0.46)), LINE.detail))
          .join('') +
        plate(body, ink) +
        plate(`M${n(cx - 112)} ${n(by)} h${n(224)} v${n(26)} h${n(-224)} Z`, PAPER.card) +
        // 추는 종에 매달려 있어야 한다. 떼어 놓으면 따로 떨어진 점으로 읽힌다.
        key(`M${n(cx)} ${n(by + 26)} v${n(26)}`, LINE.structure) +
        circle({ cx, cy: by + 64, r: 17, fill: INK.key }) +
        key(`M${n(cx - 17)} ${n(top - 20)} q${n(17)} ${n(-28)} ${n(34)} 0`, LINE.outline);
      break;
    }

    case 'ACTION': {
      // Two blades crossed. Steel prints as bare paper inside a key line.
      stage = horizonStage(0.58);
      const cy = ay(0.5);
      const blade = (): string =>
        plate(
          `M${n(cx - 15)} ${n(cy + 54)} V${n(cy - 150)} L${n(cx)} ${n(cy - 196)}` +
            ` L${n(cx + 15)} ${n(cy - 150)} V${n(cy + 54)} Z`,
          PAPER.card,
        ) +
        plate(`M${n(cx - 68)} ${n(cy + 54)} h${n(136)} v${n(24)} h${n(-136)} Z`, ink) +
        solid(`M${n(cx - 11)} ${n(cy + 78)} h${n(22)} v${n(66)} h${n(-22)} Z`) +
        plate(`M${n(cx - 19)} ${n(cy + 144)} h${n(38)} v${n(26)} h${n(-38)} Z`, ink);
      subject =
        group({ transform: `rotate(-31 ${n(cx)} ${n(cy)})` }, blade()) +
        group({ transform: `rotate(31 ${n(cx)} ${n(cy)})` }, blade());
      break;
    }

    case 'FATE': {
      // A constellation, drawn fresh per card: fate is not the same shape twice.
      stage = panelStage();
      const nodes = Array.from({ length: 7 }, (_, i) => ({
        x: ax(0.16 + (i / 6) * 0.68 + rng.range(-0.05, 0.05)),
        y: ay(0.24 + rng.range(0, 0.52)),
      }));
      const thread = nodes
        .slice(1)
        .map((p, i) => key(`M${n(nodes[i]!.x)} ${n(nodes[i]!.y)} L${n(p.x)} ${n(p.y)}`, LINE.detail))
        .join('');
      const stars = nodes
        .map((p) =>
          circle({ cx: p.x, cy: p.y, r: 11, fill: ink }) +
          circle({ cx: p.x, cy: p.y, r: 11, fill: 'none', stroke: INK.key, 'stroke-width': LINE.detail }),
        )
        .join('');
      const big = nodes[3]!;
      const burst = Array.from({ length: 8 }, (_, i) => {
        const a = (i / 8) * Math.PI * 2;
        return key(
          `M${n(big.x + Math.cos(a) * 18)} ${n(big.y + Math.sin(a) * 18)}` +
            ` L${n(big.x + Math.cos(a) * 52)} ${n(big.y + Math.sin(a) * 52)}`,
          LINE.structure,
        );
      }).join('');
      subject =
        thread + stars + burst +
        circle({ cx: big.x, cy: big.y, r: 24, fill: ink }) +
        circle({ cx: big.x, cy: big.y, r: 24, fill: 'none', stroke: INK.key, 'stroke-width': LINE.outline });
      break;
    }

    case 'KNOWLEDGE': {
      // An open book. Pages are bare paper; only the rules are inked.
      stage = panelStage();
      const cy = ay(0.5);
      const page = (s: number): string =>
        plate(
          `M${n(cx + s * 6)} ${n(cy - 126)} L${n(cx + s * 246)} ${n(cy - 92)}` +
            ` L${n(cx + s * 246)} ${n(cy + 106)} L${n(cx + s * 6)} ${n(cy + 76)} Z`,
          PAPER.card,
        ) +
        Array.from({ length: 5 }, (_, i) => {
          const t = (i + 1) / 6;
          return key(
            `M${n(cx + s * 34)} ${n(cy - 104 + t * 176)} L${n(cx + s * 216)} ${n(cy - 78 + t * 176)}`,
            LINE.detail,
          );
        }).join('');
      subject =
        page(-1) + page(1) +
        key(`M${n(cx)} ${n(cy - 128)} V${n(cy + 78)}`, LINE.outline) +
        // A ribbon marker, in the type ink — the one thing the reader put there.
        plate(
          `M${n(cx - 16)} ${n(cy + 78)} h${n(32)} v${n(96)} l${n(-16)} ${n(-28)} l${n(-16)} ${n(28)} Z`,
          ink,
        );
      break;
    }

    case 'MEMORY': {
      // An hourglass: what has already fallen is darker than what is left.
      stage = panelStage();
      const cy = ay(0.5);
      const bar = (y: number): string => solid(`M${n(cx - 128)} ${n(y)} h${n(256)} v${n(24)} h${n(-256)} Z`);
      const upper = `M${n(cx - 104)} ${n(cy - 148)} h${n(208)} L${n(cx + 9)} ${n(cy - 4)} h${n(-18)} Z`;
      const lower = `M${n(cx - 104)} ${n(cy + 148)} h${n(208)} L${n(cx + 9)} ${n(cy + 4)} h${n(-18)} Z`;
      subject =
        // 남은 모래는 옅은 망점, 떨어진 모래는 짙은 망점. 납작한 회색으로 채우면
        // 인쇄가 아니라 렌더링으로 보인다 (§8.6). 두 판은 각도가 같아도 되는데,
        // 서로 겹치지 않는 영역이라 도트가 포개질 일이 없다.
        path(upper, { fill: `url(#${keyId})` }) + key(upper, LINE.outline) +
        path(lower, { fill: `url(#${keyDarkId})` }) + key(lower, LINE.outline) +
        bar(cy - 172) + bar(cy + 148) +
        // Grains still falling. Memory is not finished settling.
        [0.18, 0.44, 0.7].map((t) => circle({ cx, cy: cy + 14 + t * 120, r: 5, fill: INK.key })).join('') +
        // 기둥은 모래시계 윤곽 바로 바깥에만 둔다. 위아래 막대까지 잇는 세로선은
        // 사각 액자로 읽혀서 모래시계 실루엣을 지워 버렸다.
        key(`M${n(cx - 118)} ${n(cy - 148)} v${n(22)}`, LINE.structure) +
        key(`M${n(cx + 118)} ${n(cy - 148)} v${n(22)}`, LINE.structure) +
        key(`M${n(cx - 118)} ${n(cy + 126)} v${n(22)}`, LINE.structure) +
        key(`M${n(cx + 118)} ${n(cy + 126)} v${n(22)}`, LINE.structure);
      break;
    }

    case 'RELATIONSHIP': {
      // Two people facing each other, printed on different plates, tied by a cord.
      // One is black, one is colour: a relationship has two sides, not one.
      // 지평선은 머리 위로 지나가야 한다. 0.62 에 두었더니 선이 두 사람의
      // 머리 한가운데를 가로질러 얼굴을 반으로 잘라 놓았다.
      stage = horizonStage(0.46);
      const base = ay(0.88);
      const bust = (s: number): string => {
        const hx = cx + s * 132;
        return (
          `M${n(hx - 76)} ${n(base)} q${n(4)} ${n(-86)} ${n(76)} ${n(-96)}` +
          ` q${n(72)} ${n(10)} ${n(76)} ${n(96)} Z`
        );
      };
      // 머리는 어깨 높이에서 역산한다. 절대좌표로 두었더니 어깨 위 50px 에 떠서
      // 원 2개와 반구 2개로 읽혔다 — 사람으로 보이지 않았다.
      const head = (s: number): { x: number; y: number } => ({ x: cx + s * 132, y: base - 130 });
      const l = head(-1);
      const r = head(1);
      subject =
        solid(bust(-1)) + circle({ cx: l.x, cy: l.y, r: 56, fill: INK.key }) +
        plate(bust(1), ink) +
        circle({ cx: r.x, cy: r.y, r: 56, fill: ink }) +
        circle({ cx: r.x, cy: r.y, r: 56, fill: 'none', stroke: INK.key, 'stroke-width': LINE.outline }) +
        // The cord between them, knotted in the middle.
        key(
          `M${n(l.x + 58)} ${n(base - 62)} Q${n(cx)} ${n(base - 18)} ${n(r.x - 58)} ${n(base - 62)}`,
          LINE.structure,
        ) +
        circle({ cx, cy: base - 34, r: 15, fill: PAPER.card }) +
        circle({ cx, cy: base - 34, r: 15, fill: 'none', stroke: INK.key, 'stroke-width': LINE.structure });
      break;
    }

    case 'CURSE': {
      // A cracked skull. The crack is the curse; it is on the colour plate so it
      // reads as damage done to the print itself.
      stage = panelStage();
      const cy = ay(0.46);
      const cranium = `M${n(cx - 122)} ${n(cy + 34)} a${n(122)} ${n(126)} 0 1 1 ${n(244)} 0 Z`;
      subject =
        plate(cranium, PAPER.card) +
        plate(`M${n(cx - 62)} ${n(cy + 30)} h${n(124)} v${n(84)} q${n(-62)} ${n(28)} ${n(-124)} 0 Z`, PAPER.card) +
        ellipse({ cx: cx - 52, cy: cy - 18, rx: 34, ry: 40, fill: INK.key }) +
        ellipse({ cx: cx + 52, cy: cy - 18, rx: 34, ry: 40, fill: INK.key }) +
        solid(`M${n(cx)} ${n(cy + 6)} l${n(-20)} ${n(38)} h${n(40)} Z`) +
        [-42, -14, 14, 42]
          .map((dx) => key(`M${n(cx + dx)} ${n(cy + 42)} v${n(58)}`, LINE.structure))
          .join('') +
        // 시작점이 두개골 바깥이면 균열이 아니라 배경에 그은 줄로 보인다.
        path(crackPath(rng, cx - 58, cy - 62, cx + 26, cy + 96, 7, 13), {
          fill: 'none',
          stroke: ink,
          'stroke-width': LINE.structure,
          'stroke-linejoin': 'round',
        }) +
        path(crackPath(rng, cx + 82, cy - 46, cx + 14, cy - 86, 5, 10), {
          fill: 'none',
          stroke: ink,
          'stroke-width': LINE.structure,
          'stroke-linejoin': 'round',
        });
      break;
    }

    case 'LEGEND': {
      // The Hollow Crown, with the rays a press used to mean "this one matters".
      stage = horizonStage(0.7);
      const cy = ay(0.5);
      const rays = Array.from({ length: 14 }, (_, i) => {
        const a = Math.PI + (i / 13) * Math.PI;
        return key(
          `M${n(cx + Math.cos(a) * 150)} ${n(cy + Math.sin(a) * 150)}` +
            ` L${n(cx + Math.cos(a) * 268)} ${n(cy + Math.sin(a) * 268)}`,
          i % 2 === 0 ? LINE.structure : LINE.detail,
        );
      }).join('');
      const crown =
        `M${n(cx - 138)} ${n(cy + 70)} V${n(cy - 42)} L${n(cx - 69)} ${n(cy + 16)}` +
        ` L${n(cx)} ${n(cy - 86)} L${n(cx + 69)} ${n(cy + 16)} L${n(cx + 138)} ${n(cy - 42)}` +
        ` V${n(cy + 70)} Z`;
      subject =
        rays +
        plate(crown, ink) +
        plate(`M${n(cx - 138)} ${n(cy + 70)} h${n(276)} v${n(36)} h${n(-276)} Z`, PAPER.card) +
        [-92, 0, 92].map((dx) => circle({ cx: cx + dx, cy: cy + 88, r: 11, fill: INK.key })).join('') +
        // Hollow: the band is empty where a head would be.
        key(`M${n(cx - 100)} ${n(cy + 70)} q${n(100)} ${n(-44)} ${n(200)} 0`, LINE.detail);
      break;
    }
  }

  const label = text('ART SLOT', {
    x: cx,
    y: A.y + A.h - 18,
    'text-anchor': 'middle',
    'font-family': 'Noto Sans KR, system-ui, sans-serif',
    'font-size': 19,
    'font-weight': 700,
    fill: INK.faded,
    'letter-spacing': 6,
  });

  return screens + stage + subject + label;
}

/** Overlays express card transformation without new artwork (Style Bible §4). */
function overlayLayer(overlay: CardOverlay, seed: string): string {
  const { width: W, height: H } = CARD_SPEC;
  if (!overlay) return '';
  const rng = new ArtRng(`overlay:${seed}`);

  switch (overlay) {
    case 'saved':
      // A gold ribbon across the top-left corner.
      return (
        polygon(
          [
            [0, 150],
            [150, 0],
            [230, 0],
            [0, 230],
          ],
          { fill: SPOT.yellow, opacity: 0.88 },
        ) +
        text('SAVED', {
          x: 84,
          y: 96,
          'text-anchor': 'middle',
          transform: 'rotate(-45 84 96)',
          'font-family': 'Noto Serif KR, Georgia, serif',
          'font-size': 26,
          fill: INK.key,
          'letter-spacing': 3,
        })
      );
    case 'fallen': {
      // Soot wash plus a torn lower edge.
      const tear = path(
        `M0 ${n(H * 0.74)} ` +
          Array.from({ length: 12 }, (_, i) =>
            `L${n((W / 12) * (i + 1))} ${n(H * 0.74 + rng.range(-26, 26))}`,
          ).join(' ') +
          ` L${n(W)} ${n(H)} L0 ${n(H)} Z`,
        { fill: INK.key, opacity: 0.72 },
      );
      return tag('rect', { width: W, height: H, fill: INK.key, opacity: 0.3 }) + tear;
    }
    case 'cursed':
      return (
        tag('rect', { width: W, height: H, fill: '#3A2028', opacity: 0.32 }) +
        Array.from({ length: 5 }, () =>
          path(crackPath(rng, rng.range(0, W), 0, rng.range(0, W), H, 9, 34), {
            fill: 'none',
            stroke: '#C4483A',
            'stroke-width': LINE.detail,
            opacity: 0.5,
          }),
        ).join('')
      );
    case 'foil':
      // Diagonal sheen bands; the animated version is a CSS transform in the client.
      return Array.from({ length: 7 }, (_, i) =>
        polygon(
          [
            [i * 130 - 200, H],
            [i * 130 - 120, H],
            [i * 130 + 240, 0],
            [i * 130 + 160, 0],
          ],
          { fill: PAPER.card, opacity: 0.07 },
        ),
      ).join('');
  }
}

export function renderCard(opts: CardRenderOptions): string {
  const { type, rarity, name, text: bodyText, overlay = null } = opts;
  const seed = opts.seed ?? `${type}:${rarity}:${name}`;
  const { width: W, height: H, radius, frameInset: F, artwork, titlePlate, bodyPlate, cornerIcon } = CARD_SPEC;
  const accent = CARD_TYPE_INK[type];
  const paperId = 'paper';

  const titleY = artwork.y + artwork.h;
  const bodyY = titleY + titlePlate.h;

  const head = defs(paperTexture(paperId, seed));

  const back =
    path(roundedRectPath(0, 0, W, H, radius), { fill: PAPER.card }) +
    path(roundedRectPath(0, 0, W, H, radius), { fill: `url(#${paperId})` }) +
    // Trimmed-edge key line: a printed card has a visible cut edge, not a fade.
    path(roundedRectPath(0, 0, W, H, radius), {
      fill: 'none',
      stroke: INK.key,
      'stroke-width': LINE.structure,
      opacity: 0.55,
    });

  const art = opts.artHref
    ? tag('image', {
        href: opts.artHref,
        x: artwork.x,
        y: artwork.y,
        width: artwork.w,
        height: artwork.h,
        preserveAspectRatio: 'xMidYMid slice',
      })
    : artworkPlaceholder(type, seed);

  const artFrame = tag('rect', {
    x: artwork.x,
    y: artwork.y,
    width: artwork.w,
    height: artwork.h,
    fill: 'none',
    stroke: INK.text,
    'stroke-width': LINE.structure,
  });

  const plates =
    tag('rect', { x: F, y: titleY, width: W - F * 2, height: titlePlate.h, fill: shade(PAPER.aged, -0.03) }) +
    tag('rect', { x: F, y: titleY, width: W - F * 2, height: titlePlate.h, fill: 'none', stroke: accent, 'stroke-width': LINE.structure }) +
    tag('rect', { x: F, y: bodyY, width: W - F * 2, height: bodyPlate.h, fill: PAPER.aged, opacity: 0.55 }) +
    tag('rect', { x: F, y: bodyY, width: W - F * 2, height: bodyPlate.h, fill: 'none', stroke: INK.text, 'stroke-width': LINE.detail });

  const titleText = text(name, {
    x: F + 22,
    y: titleY + titlePlate.h / 2 + 13,
    'font-family': 'Noto Serif KR, Georgia, serif',
    'font-size': 38,
    fill: INK.key,
  });

  // Wrapped body text. Korean does not break on spaces, so wrap by character
  // count — crude but correct for this script, unlike a space-based splitter.
  const lines = wrapText(bodyText, 22);
  const bodyLines = lines
    .slice(0, 7)
    .map((l, i) =>
      text(l, {
        x: F + 22,
        y: bodyY + 44 + i * 34,
        'font-family': 'Noto Sans KR, system-ui, sans-serif',
        'font-size': 24,
        fill: INK.text,
      }),
    )
    .join('');

  const typeIcon = group(
    { transform: `translate(${n(F + 8)} ${n(H - F - cornerIcon - 4)}) scale(${n(cornerIcon / 32)})` },
    path(TYPE_GLYPH[type], { fill: 'none', stroke: accent, 'stroke-width': 2.2, 'stroke-linejoin': 'round' }),
  );
  const rarityMark = group(
    { transform: `translate(${n(W - F - cornerIcon - 8)} ${n(H - F - cornerIcon - 4)})` },
    circle({ cx: cornerIcon / 2, cy: cornerIcon / 2, r: cornerIcon / 2 - 3, fill: RARITY[rarity].color }) +
      circle({ cx: cornerIcon / 2, cy: cornerIcon / 2, r: cornerIcon / 2 - 8, fill: 'none', stroke: PAPER.card, 'stroke-width': 1.6 }),
  );
  const typeLabel = text(type, {
    x: W / 2,
    y: H - F - 14,
    'text-anchor': 'middle',
    'font-family': 'Noto Sans KR, system-ui, sans-serif',
    'font-size': 15,
    fill: INK.faded,
    'letter-spacing': 3,
  });

  const body =
    back +
    art +
    artFrame +
    plates +
    titleText +
    bodyLines +
    rarityFrame(rarity, seed) +
    typeIcon +
    rarityMark +
    typeLabel +
    overlayLayer(overlay, seed);

  const scale = opts.scale ?? 1;
  return svg(W * scale, H * scale, body, { viewBox: `0 0 ${n(W)} ${n(H)}` });
}

/** Character-count wrapping. Correct for Korean, adequate for Latin here. */
export function wrapText(input: string, perLine: number): string[] {
  const out: string[] = [];
  for (const paragraph of input.split('\n')) {
    let line = '';
    for (const ch of paragraph) {
      line += ch;
      if (line.length >= perLine) {
        out.push(line);
        line = '';
      }
    }
    if (line.length > 0) out.push(line);
  }
  return out;
}

/**
 * Every card type, in one place. `artworkPlaceholder` owes each of these its own
 * subject, and the test that enforces that reads this list rather than repeating it.
 */
export const ALL_CARD_TYPES: readonly CardType[] = [
  'EVENT', 'ACTION', 'FATE', 'KNOWLEDGE', 'MEMORY', 'RELATIONSHIP', 'CURSE', 'LEGEND',
];

/** One frame per (type, rarity) pair, plus the four overlays. */
export function allCardFrames(): { id: string; svg: string }[] {
  const rarities: Rarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'];
  const out: { id: string; svg: string }[] = [];
  for (const type of ALL_CARD_TYPES) {
    for (const rarity of rarities) {
      out.push({
        id: `cardframe.${type.toLowerCase()}.${rarity}`,
        svg: renderCard({ type, rarity, name: '카드 이름', text: '카드 설명이 여기에 들어간다. 규격 확인용 프레임이다.' }),
      });
    }
  }
  for (const overlay of ['saved', 'fallen', 'cursed', 'foil'] as CardOverlay[]) {
    out.push({
      id: `cardoverlay.${overlay}`,
      svg: renderCard({ type: 'EVENT', rarity: 'rare', name: '변형 카드', text: '오버레이 확인용.', overlay }),
    });
  }
  return out;
}
